import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "sonner";
import ChallengeCard from "./ChallengeCard";
import ChallengeModal from "./ChallengeModal";
import SubmitChallengeDialog from "./SubmitChallengeDialog";
import FeaturedChallengeSpotlight from "./FeaturedChallengeSpotlight";
import { Button } from "@/components/ui/button";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface Challenge {
  id: string;
  title: string;
  one_liner: string | null;
  problem_description: string | null;
  who_it_affects: string | null;
  why_it_matters: string | null;
  opportunity: string | null;
  solution_directions: string[] | null;
  inputs_dependencies: string | null;
  risks_guardrails: string | null;
  success_metrics: string | null;
  references_contacts: string | null;
  difficulty: string;
  tags: string[];
  status: string;
  created_at: string;
  claimed_at: string | null;
  claimed_by?: string | null;
  completed_at: string | null;
  team_count?: number;
  update_count?: number;
  is_featured?: boolean;
  event_id?: string | null;
  vote_score?: number;
}

const COLUMNS = [
  { id: "unclaimed", title: "Open", description: "Ready for a team to claim" },
  { id: "in_progress", title: "Building", description: "Actively being worked on" },
  { id: "testing", title: "Testing", description: "Under review & validation" },
  { id: "complete", title: "Complete", description: "Successfully delivered" },
];

const DIFFICULTY_FILTERS = [
  { id: "all", label: "All Levels" },
  { id: "beginner", label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" },
];

interface ChallengeBoardProps {
  submitDialogOpen?: boolean;
  onSubmitDialogChange?: (open: boolean) => void;
}

const ChallengeBoard: React.FC<ChallengeBoardProps> = ({
  submitDialogOpen = false,
  onSubmitDialogChange,
}) => {
  const { organization } = useOrganization();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);
  const [internalDialogOpen, setInternalDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [difficultyFilter, setDifficultyFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Use external dialog state if provided, otherwise internal
  const dialogOpen = onSubmitDialogChange ? submitDialogOpen : internalDialogOpen;
  const setDialogOpen = onSubmitDialogChange || setInternalDialogOpen;

  useEffect(() => {
    // Get current user for claimant check
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id || null);
    });
  }, []);

  useEffect(() => {
    if (organization?.id) {
      fetchChallenges();
      subscribeToChanges();
    }
  }, [organization?.id]);

  const fetchChallenges = async () => {
    try {
      const { data: challengesData, error: challengesError } = await supabase
        .from("challenges")
        .select("*")
        .eq("organization_id", organization?.id)
        .order("vote_score", { ascending: false });

      if (challengesError) throw challengesError;

      const { data: teamCounts } = await supabase
        .from("challenge_team_members")
        .select("challenge_id");

      const { data: updateCounts } = await supabase
        .from("challenge_updates")
        .select("challenge_id");

      const teamCountMap: Record<string, number> = {};
      const updateCountMap: Record<string, number> = {};

      teamCounts?.forEach((tm) => {
        teamCountMap[tm.challenge_id] = (teamCountMap[tm.challenge_id] || 0) + 1;
      });

      updateCounts?.forEach((u) => {
        updateCountMap[u.challenge_id] = (updateCountMap[u.challenge_id] || 0) + 1;
      });

      const enrichedChallenges = (challengesData || []).map((c) => ({
        ...c,
        team_count: teamCountMap[c.id] || 0,
        update_count: updateCountMap[c.id] || 0,
      }));

      setChallenges(enrichedChallenges);
    } catch (error) {
      console.error("Error fetching challenges:", error);
      toast.error("Failed to load challenges");
    } finally {
      setLoading(false);
    }
  };

  const subscribeToChanges = () => {
    const channel = supabase
      .channel("challenges-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "challenges" },
        () => fetchChallenges()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "challenge_updates" },
        () => fetchChallenges()
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "challenge_team_members" },
        () => fetchChallenges()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const filteredChallenges = challenges.filter((c) => {
    const matchesSearch =
      searchQuery === "" ||
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.one_liner?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesDifficulty =
      difficultyFilter === "all" || c.difficulty === difficultyFilter;

    return matchesSearch && matchesDifficulty;
  });

  const getChallengesForColumn = (status: string) =>
    filteredChallenges.filter((c) => c.status === status && !c.is_featured);

  const hasActiveFilters = searchQuery !== "" || difficultyFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setDifficultyFilter("all");
  };

  return (
    <div className="px-4 md:px-6 py-8 max-w-7xl mx-auto">
      {/* Featured Challenge Spotlight */}
      <FeaturedChallengeSpotlight 
        onChallengeClick={(id) => setSelectedChallengeId(id)} 
      />

      {/* Compact Search & Filter Bar */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search challenges..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 bg-card/60"
            />
          </div>

          {/* Filter toggle */}
          <Button 
            variant={showFilters ? "secondary" : "outline"} 
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
          >
            <SlidersHorizontal className="h-4 w-4" />
            <span className="hidden sm:inline">Filters</span>
            {hasActiveFilters && (
              <Badge variant="secondary" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
                1
              </Badge>
            )}
          </Button>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
              <X className="h-4 w-4" />
              Clear
            </Button>
          )}
        </div>

        {/* Expandable filter chips */}
        {showFilters && (
          <div className="mt-3 flex flex-wrap gap-2">
            {DIFFICULTY_FILTERS.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setDifficultyFilter(filter.id)}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-full border transition-colors",
                  difficultyFilter === filter.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card/60 text-muted-foreground border-border hover:border-primary/50"
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tabbed Challenge View */}
      <Tabs defaultValue="unclaimed" className="w-full">
        <TabsList className="w-full justify-start bg-card/40 p-1 mb-6">
          {COLUMNS.map((column) => {
            const count = getChallengesForColumn(column.id).length;
            return (
              <TabsTrigger 
                key={column.id} 
                value={column.id} 
                className="flex items-center gap-2 data-[state=active]:bg-card"
              >
                <span>{column.title}</span>
                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {count}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {COLUMNS.map((column) => (
          <TabsContent key={column.id} value={column.id}>
            <p className="text-sm text-muted-foreground mb-5">{column.description}</p>
            
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-36 w-full" />
                ))}
              </div>
            ) : getChallengesForColumn(column.id).length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <p className="text-lg mb-2">No challenges here yet</p>
                <p className="text-sm">
                  {column.id === "unclaimed" 
                    ? "Check back soon or submit your own idea!" 
                    : "Challenges will appear here as teams make progress."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {getChallengesForColumn(column.id).map((challenge) => (
                  <ChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    onClick={() => setSelectedChallengeId(challenge.id)}
                    isClaimant={currentUserId ? challenge.claimed_by === currentUserId : false}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Challenge Detail Modal */}
      {selectedChallengeId && (
        <ChallengeModal
          challengeId={selectedChallengeId}
          open={!!selectedChallengeId}
          onOpenChange={(open) => !open && setSelectedChallengeId(null)}
          onUpdate={fetchChallenges}
        />
      )}

      {/* Submit Idea Dialog */}
      <SubmitChallengeDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  );
};

export default ChallengeBoard;
